/**
 * VerifyEmailBanner — a persistent prompt shown to a signed-in user whose
 * email address hasn't been confirmed yet (B-323).
 *
 * B-967: this used to say "Verify it to start converting", which stopped being
 * true at B-368 — the free signup credits are granted on bootstrap regardless,
 * and neither sign-in nor conversion checks `email_verified`. Telling someone
 * they're blocked when thirty seconds of clicking proves they aren't teaches
 * them to ignore our warnings, so the copy now states the real stake: an
 * unconfirmed address can't receive a password reset, and it's the only way we
 * can reach them about a conversion. Tone is `info`, not `warn` — nothing is
 * broken and nothing is blocked.
 *
 * Collapsed: a one-line prompt. Clicking it expands where the mail was sent,
 * why it's probably in spam (Firebase sends it from its own firebaseapp.com
 * sender, not our verified domain — B-970), and a resend action.
 * Renders nothing unless the current user's `emailVerified` is explicitly false.
 */

import { useState } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { ChevronDown, MailCheck } from 'lucide-react';

import { auth } from '../../lib/firebase';
import useAuthStore from '../../stores/authStore';
import { Banner } from './ui/banner';
import { Button } from './ui/button';

type SendState = 'idle' | 'sending' | 'sent' | 'error';

/** Persistent "verify your email" banner for unverified signed-in users. */
export function VerifyEmailBanner() {
  const user = useAuthStore((s) => s.user);
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState<SendState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Only an explicit `false` triggers the banner — `undefined` (dev bypass,
  // pre-load) must not flash a false alarm at a verified user.
  if (!user || user.emailVerified !== false) return null;

  const resend = async () => {
    const current = auth.currentUser;
    if (!current) return;
    setState('sending');
    setErrorMsg(null);
    try {
      await sendEmailVerification(current);
      setState('sent');
    } catch (err) {
      // Firebase throttles repeat sends; surface a friendly, actionable line.
      const code = (err as { code?: string }).code;
      setState('error');
      setErrorMsg(
        code === 'auth/too-many-requests'
          ? 'Too many requests — please wait a minute and try again.'
          : 'We couldn’t send the email just now. Please try again in a moment.',
      );
    }
  };

  return (
    <div className="px-5 pt-3">
      <Banner tone="info" icon={<MailCheck />}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex w-full items-center gap-2 text-left"
        >
          <span className="font-[560] text-[var(--tx-hi)]">
            Confirm your email address.
          </span>
          <span className="text-[var(--tx-mid)]">
            It’s what lets you reset your password later.
          </span>
          <ChevronDown
            className={`ml-auto h-[15px] w-[15px] shrink-0 transition-transform ${
              expanded ? 'rotate-180' : ''
            }`}
          />
        </button>

        {expanded && (
          <div className="mt-2 space-y-2 text-[12.5px] text-[var(--tx-mid)]">
            <p>
              Your credits are already on your account and everything works — this
              isn’t blocking you. Confirming just means we can reset your password
              if you ever forget it, and reach you about a conversion.
            </p>
            <p>
              We sent a link
              {user.email ? (
                <>
                  {' '}
                  to <span className="text-[var(--tx-hi)]">{user.email}</span>
                </>
              ) : null}
              . It comes from <span className="text-[var(--tx-hi)]">
                noreply@aiea-app.firebaseapp.com
              </span>, so check your spam folder before resending.
            </p>
            {state === 'sent' ? (
              <p className="font-[560] text-[var(--ok)]">
                Confirmation email sent — check your inbox (and spam).
              </p>
            ) : (
              <p>
                Can’t find it?{' '}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={resend}
                  disabled={state === 'sending'}
                >
                  {state === 'sending' ? 'Sending…' : 'Resend confirmation email'}
                </Button>
              </p>
            )}
            {state === 'error' && errorMsg && (
              <p className="font-medium text-[var(--bad)]">{errorMsg}</p>
            )}
          </div>
        )}
      </Banner>
    </div>
  );
}

export default VerifyEmailBanner;
